<template>
  <div
    :key="String(isMounted)"
    class="py-2"
    :style="bgStyle()"
    data-test-id="reviews-by-partner-id"
  >
    <HorizontalCarouselWrapper
      :items-length="computedReviewsList.length"
      @scroll:right="carouselIdx = carouselIdx + 1"
      @scroll:left="carouselIdx--"
    >
      <ReviewCard
        v-if="computedReviewsList[0]"
        class="min-[1000px]:w-full min-h-[200px] hidden min-[1000px]:block"
        data-test-id="review-card-1"
        :average-rating="computedReviewsList[0].rating"
        :reviewer-name="computedReviewsList[0].customer"
        :is-teamevent="false"
        :reviewed-at="
          dateToFormat(
            computedReviewsList[0].created_at,
            'DD.MM.YYYY',
            $i18n.locale
          )
        "
        :review-content="computedReviewsList[0].review"
        :supplier-reply="computedReviewsList[0].supplier_reply"
      />
      <ReviewCard
        v-if="computedReviewsList[1]"
        class="min-[1000px]:w-full min-h-[200px]"
        data-test-id="review-card-2"
        :average-rating="computedReviewsList[1].rating"
        :reviewer-name="computedReviewsList[1].customer"
        :is-teamevent="false"
        :reviewed-at="
          dateToFormat(
            computedReviewsList[1]?.created_at,
            'DD.MM.YYYY',
            $i18n.locale
          )
        "
        :review-content="computedReviewsList[1].review"
        :supplier-reply="computedReviewsList[1].supplier_reply"
      />
      <ReviewCard
        v-if="computedReviewsList[2]"
        class="min-[1000px]:w-full min-h-[200px] hidden min-[1000px]:block"
        data-test-id="review-card-3"
        :average-rating="computedReviewsList[2].rating"
        :reviewer-name="computedReviewsList[2].customer"
        :is-teamevent="false"
        :reviewed-at="
          dateToFormat(
            computedReviewsList[2].created_at,
            'DD.MM.YYYY',
            $i18n.locale
          )
        "
        :review-content="computedReviewsList[2].review"
        :supplier-reply="computedReviewsList[2].supplier_reply"
      />
    </HorizontalCarouselWrapper>

    <div class="flex w-full" :class="alignClass()">
      <WidgetGeneralBanner
        class="cursor-default w-fit p-2.5 md:p-3 bg-white items-center rounded-lg border-stone-200 border"
        :average-rating="partnerData.avg_review"
        :total-reviews="partnerData.total_reviews"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type { Review, Supplier } from '~/composables/types/event'
import HorizontalCarouselWrapper from '~/components/common/HorizontalCarouselWrapper.vue'

const route = useRoute()
const { alignClass, bgStyle, isMounted } = useWidgetAlign()

const partnerId = route.params.partnerId

definePageMeta({
  layout: 'blank',
})

const { data: partnerResponse = undefined } = await useKftApiFetch<
  KftApiDefaultResponse<Supplier>,
  {
    avg_review: number
    total_reviews: number
    reviews: Review[]
  }
>(`v1/store/supplier/${partnerId}?include=reviews`, {
  transform: (data) => {
    return {
      avg_review: data?.data?.avg_review,
      total_reviews: data?.data?.total_reviews,
      reviews: data?.data?.reviews?.data || [],
    }
  },
})

const partnerData = partnerResponse?.value

if (!partnerData) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

const carouselIdx = ref(0)

const reviews = computed(() =>
  [...(partnerData.reviews ?? [])].sort((a, b) =>
    dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? 1 : -1
  )
)

const computedReviewsList = computed(() => {
  const length = reviews.value.length
  const adjustedIdx = (idx) => (length + (idx % length)) % length

  return [
    reviews.value[adjustedIdx(0 + carouselIdx.value)],
    reviews.value[adjustedIdx(1 + carouselIdx.value)],
    reviews.value[adjustedIdx(2 + carouselIdx.value)],
  ]
})
</script>
